// A lost identity cookie cannot reclaim an Insights alias owned by another
// root. Replace the source id, never the visitor credential or old history.
// The journal makes a response loss/reload retry the same replacement.
const ALIAS_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const JOURNAL_PREFIX = '__milla_identity_alias_replacement__:';
const ADOPTED_PREFIX = '__milla_identity_alias_adopted__:';
const accepted = (result) => result && ['ADOPTED', 'ALREADY_PRESENT'].includes(result.status);
const journalKey = (brand) => `${JOURNAL_PREFIX}${brand}`;

// Adopted under which credential (2026-10-05). The bootstrap answer carries
// `adoptTag`, a one-way tag of the visitor credential it left in place. A
// 53-bit digest of tag and alias (cyrb53; neither is stored) marks "this
// alias is on this credential's root", so later pages skip a request that
// answered ALREADY_PRESENT about half the time. A new, rotated or reset
// credential changes the tag, and the alias is adopted again.
function adoptedDigest(tag, alias) {
  const text = `${tag}|${alias}`;
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

function adoptedUnder(brand, tag, alias) {
  if (!tag) return false;
  try { return window.localStorage.getItem(`${ADOPTED_PREFIX}${brand}`) === adoptedDigest(tag, alias); } catch (_) { return false; }
}

function rememberAdopted(brand, tag, alias) {
  if (!tag) return;
  try { window.localStorage.setItem(`${ADOPTED_PREFIX}${brand}`, adoptedDigest(tag, alias)); } catch (_) { /* next page asks again */ }
}

function pendingReplacement(brand) {
  try {
    const value = JSON.parse(window.localStorage.getItem(journalKey(brand)) || 'null');
    return value && typeof value.from === 'string' && typeof value.to === 'string'
      && ALIAS_PATTERN.test(value.from) && ALIAS_PATTERN.test(value.to) && value.from !== value.to
      && (value.supersedes === undefined || typeof value.supersedes === 'string'
        && ALIAS_PATTERN.test(value.supersedes) && value.supersedes !== value.to) ? value : null;
  } catch (_) { return null; }
}

function freshSourceId() {
  // This identifier is independent of both visitor credentials and ads ids.
  const random = typeof crypto !== 'undefined' ? crypto : null;
  if (!random || typeof random.getRandomValues !== 'function') return null;
  const bytes = random.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (n) => n.toString(16).padStart(2, '0')).join('');
}

async function adoptSourceAlias({ brand, issuer, request, isCurrent, cache = new Map(), recover = false, pageKind = null, tag = null }) {
  const key = `milla_anon:${brand}`;
  const read = () => window.localStorage.getItem(key);
  const submit = async (value, supersedes) => {
    if (!isCurrent()) return null;
    const response = await request('/__milla/identity/aliases/adopt', {
      schemaVersion: 1,
      aliases: [{ type: 'INSIGHTS_ANON_ID', value, ...(supersedes ? { supersedes } : {}) }],
      // Edge counter key only (`adopt.<status>.page.<kind>`); milla never sees it.
      ...(pageKind === 'article' || pageKind === 'other' ? { page: pageKind } : {})
    });
    return response?.results?.find((r) => r.type === 'INSIGHTS_ANON_ID') || null;
  };
  try {
    const original = read();
    if (!ALIAS_PATTERN.test(original || '') || !isCurrent()) return null;
    let pending = recover ? pendingReplacement(brand) : null;
    if (pending && ![pending.from, pending.to].includes(original)) {
      // Another tab/module already selected a different source. Never overwrite it.
      window.localStorage.removeItem(journalKey(brand));
      pending = null;
    }
    if (!pending) {
      // Cache only within this bootstrap. A restored tab can retain sessionStorage
      // after its HttpOnly credential has expired, so session markers are unsafe.
      if (cache.get(issuer) === original) return { status: 'ALREADY_PRESENT' };
      if (adoptedUnder(brand, tag, original)) {
        cache.set(issuer, original);
        return { status: 'ALREADY_PRESENT' };
      }
      const result = await submit(original);
      if (!isCurrent() || read() !== original) return null;
      if (accepted(result)) {
        cache.set(issuer, original);
        rememberAdopted(brand, tag, original);
        return result;
      }
      if (result?.status !== 'CONFLICT' || !recover) return result;
      // Another tab may have begun the same repair during our ownership request.
      // Prefer its journal so both tabs retry the same source id.
      pending = pendingReplacement(brand);
      if (!pending || ![pending.from, pending.to].includes(original)) {
        const replacement = freshSourceId();
        if (!replacement) return result;
        pending = { from: original, to: replacement };
        window.localStorage.setItem(journalKey(brand), JSON.stringify(pending));
        if (pendingReplacement(brand)?.to !== replacement) return result;
      }
    }
    const unchanged = () => isCurrent() && [pending.from, pending.to].includes(read()) && pendingReplacement(brand)?.to === pending.to;
    let adopted;
    for (let attempt = 0; attempt < 2; attempt++) {
      if (!unchanged()) return null;
      adopted = await submit(pending.to, pending.supersedes || pending.from);
      if (!unchanged()) return null;
      if (adopted?.status !== 'CONFLICT') break;
      // The credential may have changed again since this repair started. Only
      // an authoritative conflict permits another id; timeouts keep the same one.
      const replacement = freshSourceId();
      if (!replacement) return adopted;
      pending = { from: read(), to: replacement, supersedes: pending.to };
      window.localStorage.setItem(journalKey(brand), JSON.stringify(pending));
    }
    if (!accepted(adopted) || !unchanged()) return adopted;
    // Synchronize the auth-host survey cookie before a survey iframe opens.
    // Retrying an adopted replacement is safe if this request or its reply fails.
    const synced = await request('/api/insights/anon/adopt', { anonId: pending.to });
    if (synced?.success !== true || synced.anonId !== pending.to || !unchanged()) return null;
    window.localStorage.setItem(key, pending.to);
    if (read() !== pending.to) return null;
    window.localStorage.removeItem(journalKey(brand));
    cache.set(issuer, pending.to);
    rememberAdopted(brand, tag, pending.to);
    return adopted;
  } catch (_) {
    // Keep the journal for a later page; never generate another id on failure.
    return null;
  }
}

module.exports = { adoptSourceAlias, pendingReplacement };
